import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { TRIPROCK_URL } from "@/constants";
import { useLang, useTranslation } from "@/i18n";

// Values aligned by index with t.iot.statLabels
const STAT_VALUES = [
  { value: 2000, suffix: "+" },
  { value: 30, suffix: "+" },
  { value: 24, suffix: "" },
];

// Icons aligned by index with t.iot.blocks
const BLOCK_ICONS = ["sensors", "storage", "lock", "handshake"];

// Images aligned by index with t.iot.products
const PRODUCT_IMAGES = [
  "/images/products/environmental.jpg",
  "/images/products/container.jpg",
  "/images/products/waterdetector.jpg",
  "/images/products/railroad-barriers.jpg",
];

// Images aligned by index with t.iot.sensors
const SENSOR_IMAGES = [
  "/images/sensors/gsm.svg",
  "/images/sensors/gps.svg",
  "/images/sensors/temperature.svg",
  "/images/sensors/humidity.svg",
  "/images/sensors/microparticles.svg",
  "/images/sensors/acceleration.svg",
  "/images/sensors/vibration.svg",
  "/images/sensors/battery.svg",
  "/images/sensors/position.svg",
  "/images/sensors/geofencing.svg",
  "/images/sensors/meshnet.svg",
  "/images/sensors/water.svg",
  "/images/sensors/powerflow.svg",
  "/images/sensors/vandalism.svg",
];

const marqueeVariants = {
  animate: {
    x: [0, -1400],
    transition: {
      x: { repeat: Infinity, repeatType: "loop" as const, duration: 55, ease: "linear" },
    },
  },
};

const CountUp: React.FC<{ value: number; suffix: string; locale: string }> = ({
  value,
  suffix,
  locale,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  const reducedMotion = useReducedMotion();
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    if (reducedMotion) {
      setDisplay(value);
      return;
    }
    const duration = 1400;
    const start = performance.now();
    let frame: number;
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * value));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [isInView, value, reducedMotion]);

  return (
    <span ref={ref}>
      {display.toLocaleString(locale)}
      {suffix}
    </span>
  );
};

export const IoT: React.FC = () => {
  const t = useTranslation();
  const reducedMotion = useReducedMotion();
  const locale = useLang() === "de" ? "de-DE" : "en-US";

  const sensorMarquee = t.iot.sensors.map((name, index) => ({
    name,
    image: SENSOR_IMAGES[index],
  }));

  return (
    <>
      <div id="iot-triprock"></div>
      <div className="bg-[#1d2128] py-[72px] md:py-[120px] overflow-hidden">
        <div className="max-w-screen-xl m-auto px-[38px] 2xl:px-0">
          <p className="text-[#01C6FB] font-semibold tracking-[0.18em] uppercase text-[13px] md:text-[14px]">
            {t.iot.overline}
          </p>
          <h2 className="mt-3 text-white font-bold text-[28px] leading-[38px] md:text-[44px] md:leading-[56px] max-w-4xl">
            {t.iot.headlinePlain}{" "}
            <span className="text-[#01C6FB]">{t.iot.headlineAccent}</span>
          </h2>
          <p className="mt-5 max-w-3xl text-[16px] leading-[26px] md:text-[18px] md:leading-[30px] text-base-300">
            {t.iot.intro}
          </p>

          {/* Stat band */}
          <div className="mt-[48px] md:mt-[64px] grid grid-cols-1 sm:grid-cols-3 gap-8 border-y border-white/10 py-[32px] md:py-[40px]">
            {t.iot.statLabels.map((label, index) => (
              <div key={label} className="text-center">
                <p className="text-[40px] md:text-[56px] font-bold text-[#01C6FB] leading-none">
                  <CountUp
                    value={STAT_VALUES[index].value}
                    suffix={STAT_VALUES[index].suffix}
                    locale={locale}
                  />
                </p>
                <p className="mt-3 text-[14px] md:text-[15px] text-base-300">
                  {label}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-[48px] md:mt-[64px] flex flex-col lg:flex-row gap-[48px] lg:gap-[64px]">
            {/* Feature blocks */}
            <div className="w-full lg:w-3/5 grid grid-cols-1 md:grid-cols-2 gap-8">
              {t.iot.blocks.map((block, index) => (
                <motion.div
                  key={block.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: (index % 2) * 0.1 }}
                >
                  <span className="material-symbols-outlined text-[32px] text-[#01C6FB]">
                    {BLOCK_ICONS[index]}
                  </span>
                  <h3 className="mt-3 text-[18px] md:text-[20px] font-bold text-white">
                    {block.title}
                  </h3>
                  <p className="mt-2 text-[15px] leading-[25px] text-base-300">
                    {block.text}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* TripRock product showcase */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="w-full lg:w-2/5"
            >
              <div className="rounded-[20px] bg-white/5 border border-white/10 p-[24px] md:p-[28px]">
                <p className="text-[13px] font-semibold tracking-[0.15em] uppercase text-[#01C6FB]">
                  {t.iot.showcaseLabel}
                </p>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {t.iot.products.map((name, index) => (
                    <a
                      key={name}
                      href={TRIPROCK_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="group"
                    >
                      <div className="overflow-hidden rounded-[12px]">
                        <img
                          src={PRODUCT_IMAGES[index]}
                          alt={name}
                          loading="lazy"
                          className="h-[90px] md:h-[110px] w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      </div>
                      <p className="mt-2 text-[13px] md:text-[14px] text-base-300 group-hover:text-white transition-colors">
                        {name}
                      </p>
                    </a>
                  ))}
                </div>
                <a
                  href={TRIPROCK_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-block text-[15px] font-semibold text-[#01C6FB] hover:underline"
                >
                  {t.iot.showcaseLink}
                </a>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Sensor marquee */}
        <div className="mt-[56px] md:mt-[72px]">
          <p className="text-center text-[14px] text-base-400 px-[38px]">
            {t.iot.marqueeHeading}
          </p>
          <motion.div
            className="mt-[20px] flex flex-row items-center"
            variants={marqueeVariants}
            animate={reducedMotion ? undefined : "animate"}
          >
            {[...sensorMarquee, ...sensorMarquee, ...sensorMarquee].map(
              (sensor, index) => (
                <div
                  key={`sensor-${index}`}
                  className="flex flex-col min-w-32 items-center text-center text-[13px] text-base-400"
                >
                  <img
                    src={sensor.image}
                    alt={sensor.name}
                    loading="lazy"
                    className="w-[48px] h-[48px] invert opacity-70"
                  />
                  {sensor.name}
                </div>
              ),
            )}
          </motion.div>
        </div>

        {/* Brand link */}
        <div className="max-w-screen-xl m-auto px-[38px] 2xl:px-0 mt-[56px] md:mt-[64px] text-[15px] text-base-400 text-center">
          {t.iot.footerLine}{" "}
          <a
            href={TRIPROCK_URL}
            target="_blank"
            rel="noreferrer"
            className="text-[#01C6FB] font-semibold hover:underline"
          >
            triprock.de
          </a>
        </div>
      </div>
    </>
  );
};
